import escapeHtml from "escape-html";
import type { EChartsOption } from "echarts";
import type { BarItem } from "@/pages/CampaignOverviewPage/charts/DeviceBrowserChart/useDeviceBrowserData";
import {
  BAR_BORDER_RADIUS_VERTICAL,
  buildAxisLabelStyle,
  buildLegendTextStyle,
  buildTooltipStyle,
  buildTooltipValueStyle,
  chartGridLineColor,
  CHART_FONT_FAMILY,
  CONTAIN_AXIS_LABELS,
  formatImpressionAxis,
  getChartTextColors,
  IMPRESSION_FORMATTER,
  LEGEND_BASE,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import { getChartPalette } from "@/pages/CampaignOverviewPage/charts/chartPalette";

const truncate = (s: string): string =>
  s.length > 12 ? `${s.slice(0, 12)}…` : s;

export const buildDeviceBrowserOption = (
  bars: BarItem[],
  isDark: boolean
): EChartsOption => {
  const axisLabelStyle = buildAxisLabelStyle(isDark);
  const legendTextStyle = buildLegendTextStyle(isDark);
  const { secondary } = getChartTextColors(isDark);

  const labels = bars.map((bar) => bar.label);
  const impData = bars.map((bar) => bar.impressions);
  const ctrData = bars.map((bar) =>
    bar.ctr !== null && bar.ctr > 0 ? bar.ctr : null
  );

  const hasCtrData = bars.some((bar) => bar.ctr !== null && bar.ctr > 0);
  const maxCtr = hasCtrData
    ? Math.max(
        ...bars
          .filter((bar): bar is BarItem & { ctr: number } => bar.ctr !== null)
          .map((bar) => bar.ctr)
      ) * 1.15
    : 0;

  const gridLineColor = chartGridLineColor(isDark);
  const palette = getChartPalette(isDark);
  const valueStyle = buildTooltipValueStyle(isDark);

  return {
    grid: {
      bottom: 56,
      ...CONTAIN_AXIS_LABELS,
      left: 58,
      right: hasCtrData ? 64 : 20,
      top: 36,
    },
    legend: {
      ...LEGEND_BASE,
      // Flat swatches even though the bars draw a gradient.
      data: [
        {
          icon: "roundRect",
          itemStyle: { color: palette.primary },
          name: "Impressions",
        },
        ...(hasCtrData
          ? [
              {
                icon: "circle",
                itemStyle: { color: palette.orange },
                name: "CTR",
              },
            ]
          : []),
      ],
      textStyle: legendTextStyle,
    },
    series: [
      {
        barMaxWidth: 72,
        data: impData,
        itemStyle: {
          borderRadius: BAR_BORDER_RADIUS_VERTICAL,
          color: palette.barGradientV,
        },
        name: "Impressions",
        type: "bar",
        yAxisIndex: 0,
      },
      ...(hasCtrData
        ? [
            {
              connectNulls: false,
              data: ctrData,
              itemStyle: { color: palette.orange },
              lineStyle: { color: palette.orange, width: 2.5 },
              name: "CTR",
              showSymbol: true,
              symbol: "circle",
              symbolSize: 8,
              type: "line" as const,
              yAxisIndex: 1,
            },
          ]
        : []),
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      axisPointer: { type: "shadow" },
      formatter: (params: unknown) => {
        const items = params as {
          marker: string;
          name: string;
          seriesIndex: number;
          value: number | null;
        }[];
        if (!items.length) return "";

        const barIndex = labels.indexOf(items[0]?.name ?? "");
        const bar = barIndex >= 0 ? bars[barIndex] : undefined;
        const impItem = items.find((param) => param.seriesIndex === 0);
        const ctrItem = items.find((param) => param.seriesIndex === 1);

        let html = `<div style="font-weight:600;margin-bottom:4px;font-family:${CHART_FONT_FAMILY}">${escapeHtml(items[0]?.name ?? "")}</div>`;
        if (impItem) {
          html += `${impItem.marker} Impressions: <b style="${valueStyle}">${IMPRESSION_FORMATTER.format(impItem.value ?? 0)}</b>`;
        }
        if (bar?.ctr !== null && ctrItem?.value != null) {
          html += `<br/>${ctrItem.marker} CTR: <b style="${valueStyle}">${(ctrItem.value * 100).toFixed(2)}%</b>`;
        } else if (bar && bar.ctr === null) {
          html += `<br/><span style="color:${secondary}">CTR: N/A</span>`;
        }
        return html;
      },
      trigger: "axis",
    },
    xAxis: {
      axisLabel: {
        ...axisLabelStyle,
        formatter: (value: string) => truncate(value),
        interval: 0,
      },
      axisLine: { lineStyle: { color: gridLineColor } },
      axisTick: { show: false },
      data: labels,
      type: "category",
    },
    yAxis: [
      {
        axisLabel: { ...axisLabelStyle, formatter: formatImpressionAxis },
        axisLine: { show: false },
        axisTick: { show: false },
        position: "left",
        splitLine: { lineStyle: { color: gridLineColor } },
        splitNumber: 5,
        type: "value",
      },
      ...(hasCtrData
        ? [
            {
              axisLabel: {
                ...axisLabelStyle,
                formatter: (value: number) => `${(value * 100).toFixed(2)}%`,
              },
              axisLine: { show: false },
              axisTick: { show: false },
              max: maxCtr,
              min: 0,
              position: "right" as const,
              splitLine: { show: false },
              splitNumber: 5,
              type: "value" as const,
            },
          ]
        : []),
    ],
  };
};
